import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inr, pct, xx } from '@krit/core';
import { listDeals, getPortfolio, getSettings } from '../lib/api.js';
import { Card, StatTile, PageHeader, Pill } from '../components/ui.js';

/** Fund corpus target (₹ Cr). Matches the sidebar; make it a setting later if needed. */
const FUND_TARGET = 150;

const BAR: Record<string, string> = { teal: 'bg-teal', green: 'bg-green', gold: 'bg-gold', purple: 'bg-purple', navy: 'bg-navy', red: 'bg-red', amber: 'bg-amber' };

/** Horizontal bar row: label, value, and a proportional bar. */
function BarRow({ label, value, max, display, tone = 'teal' }: { label: string; value: number; max: number; display: string; tone?: string }) {
  const w = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-40 shrink-0 text-[12px] text-navy truncate">{label}</div>
      <div className="flex-1 h-4 bg-line rounded-md overflow-hidden"><div className={`h-full rounded-md ${BAR[tone] ?? BAR.teal}`} style={{ width: `${Math.max(2, w)}%` }} /></div>
      <div className="w-24 shrink-0 text-right text-[12px] font-bold text-navy mono">{display}</div>
    </div>
  );
}

const STAGE_ORDER = ['Open', 'Under Review', 'Interested', 'In Discussion', 'Diligence', 'IC Review', 'Term Sheet', 'Invested'];

export function Analytics() {
  const { data: dealsData } = useQuery({ queryKey: ['deals'], queryFn: listDeals, retry: 2, retryDelay: 800 });
  const { data: pf } = useQuery({ queryKey: ['portfolio'], queryFn: getPortfolio, retry: 2, retryDelay: 800 });
  const { data: sData } = useQuery({ queryKey: ['settings'], queryFn: getSettings, retry: 1, staleTime: 60_000 });

  const deals = useMemo(() => dealsData?.deals ?? [], [dealsData]);
  const holdings = pf?.holdings ?? [];
  const r = pf?.rollups;
  const target = FUND_TARGET;

  const invested = r?.invested ?? 0;
  const dryPowder = Math.max(0, target - invested);
  const deployedPct = target ? Math.round((invested / target) * 100) : 0;
  const allocSought = deals.reduce((a, d) => a + (d.allocation || 0), 0);

  // Sector concentration by allocation (₹ Cr).
  const bySector = useMemo(() => {
    const m = new Map<string, number>();
    deals.forEach((d) => m.set(d.sector, (m.get(d.sector) ?? 0) + (d.allocation || 0)));
    return [...m.entries()].map(([sector, alloc]) => ({ sector, alloc })).sort((a, b) => b.alloc - a.alloc);
  }, [deals]);
  const sectorMax = Math.max(1, ...bySector.map((s) => s.alloc));
  const sectorTotal = bySector.reduce((a, s) => a + s.alloc, 0);

  // Pipeline funnel by stage (count).
  const byStage = STAGE_ORDER.map((st) => ({ st, n: deals.filter((d) => d.status === st).length })).filter((x) => x.n > 0);
  const stageMax = Math.max(1, ...byStage.map((x) => x.n));

  // Thesis-fit split.
  const fit = deals.filter((d) => d.computed.thesisFit === 'Fit').length;
  const borderline = deals.filter((d) => d.computed.thesisFit === 'Borderline').length;
  const pass = deals.filter((d) => d.computed.thesisFit === 'Pass').length;

  const icThreshold = sData?.settings?.icThreshold ?? 70;
  const clearing = deals.filter((d) => d.computed.score >= icThreshold).length;
  const avgScore = deals.length ? Math.round(deals.reduce((a, d) => a + d.computed.score, 0) / deals.length) : 0;

  return (
    <div>
      <PageHeader eyebrow="Portfolio" title="Fund Analytics" desc={`Krit Fund I · target ₹${target} Cr · ${deals.length} pipeline deals · ${holdings.length} holdings`} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatTile label="Deployed" value={inr(invested)} note={`${deployedPct}% of ₹${target} Cr`} accent="teal" />
        <StatTile label="Dry powder" value={inr(dryPowder)} note="undeployed" accent="navy" />
        <StatTile label="Current mark" value={inr(r?.mark ?? 0)} note="fair value" accent="green" />
        <StatTile label="Blended MOIC" value={r?.blendedMOIC ? xx(r.blendedMOIC) : '—'} note="mark ÷ invested" accent="gold" />
        <StatTile label="Alloc sought" value={inr(allocSought)} note="live pipeline" accent="purple" />
        <StatTile label="Clearing IC" value={`${clearing}/${deals.length}`} note={`avg score ${avgScore}`} accent="amber" />
      </div>

      {/* Deployment bar */}
      <Card title="Capital deployment" sub={`₹${invested.toFixed(0)} Cr deployed · ₹${dryPowder.toFixed(0)} Cr dry powder`} className="mb-4">
        <div className="h-6 bg-line rounded-lg overflow-hidden flex">
          <div className="h-full bg-teal flex items-center justify-center text-white text-[10px] font-bold" style={{ width: `${deployedPct}%` }}>{deployedPct > 8 ? `${deployedPct}%` : ''}</div>
        </div>
        <div className="flex justify-between text-[10.5px] text-muted mt-1"><span>Deployed {inr(invested)}</span><span>Target {inr(target)}</span></div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Sector concentration" sub="By allocation across the pipeline">
          {bySector.length === 0 ? <p className="text-muted text-[12.5px] text-center py-4">No data.</p> : bySector.map((s, i) => (
            <BarRow key={s.sector} label={s.sector} value={s.alloc} max={sectorMax} tone={['teal', 'navy', 'gold', 'purple', 'green', 'amber'][i % 6]}
              display={`${inr(s.alloc)} · ${sectorTotal ? Math.round((s.alloc / sectorTotal) * 100) : 0}%`} />
          ))}
        </Card>

        <Card title="Pipeline funnel" sub="Deals by stage">
          {byStage.length === 0 ? <p className="text-muted text-[12.5px] text-center py-4">No data.</p> : byStage.map((x) => (
            <BarRow key={x.st} label={x.st} value={x.n} max={stageMax} tone="navy" display={String(x.n)} />
          ))}
        </Card>

        <Card title="Thesis-fit split" sub={`${deals.length} pipeline deals`}>
          <div className="flex gap-4 items-center">
            <div className="flex-1 space-y-2">
              <BarRow label="Fit" value={fit} max={deals.length || 1} tone="green" display={String(fit)} />
              <BarRow label="Borderline" value={borderline} max={deals.length || 1} tone="amber" display={String(borderline)} />
              <BarRow label="Pass" value={pass} max={deals.length || 1} tone="red" display={String(pass)} />
            </div>
          </div>
        </Card>

        <Card title="Holdings — invested vs mark" sub="Blended MOIC across the book">
          {holdings.length === 0 ? <p className="text-muted text-[12.5px] text-center py-4">No holdings yet.</p> : (
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-muted text-[10.5px] uppercase"><th className="text-left font-bold py-1">Holding</th><th className="text-right font-bold">Invested</th><th className="text-right font-bold">Mark</th><th className="text-right font-bold">MOIC</th><th className="text-right font-bold">IPO</th></tr></thead>
              <tbody>
                {holdings.map((h) => (
                  <tr key={h.id} className="border-t border-line-2">
                    <td className="py-1.5 font-semibold text-navy">{h.short}</td>
                    <td className="text-right mono">{inr(h.holding.invested)}</td>
                    <td className="text-right mono">{inr(h.holding.mark)}</td>
                    <td className="text-right mono font-bold">{h.baseMOIC ? xx(h.baseMOIC) : '—'}</td>
                    <td className="text-right"><Pill tone={h.holding.ipoPct >= 60 ? 'green' : h.holding.ipoPct >= 30 ? 'amber' : 'gray'}>{h.holding.ipoPct}%</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <p className="text-[11px] text-muted-2 mt-3">Sector-cap limits, vintage cohorts, and IC voting/approvals arrive in later Phase-5 slices.</p>
    </div>
  );
}
